<template>
  <div>
    <v-card class="pa-4">
      <v-card-text>
        <v-alert type="warning" outlined>
          Ce workflow écrit en production lorsqu’il est lancé sur l’Airflow de
          production. Il exécute exactement les lots figés du plan approuvé
          sélectionné ci-dessous ; il ne relit pas les flux et ne recalcule pas
          le périmètre.
        </v-alert>

        <v-row class="mt-3">
          <v-col cols="5">Plan approuvé</v-col>
          <v-col cols="7">
            <v-select
              v-model="selectedPlanId"
              :items="planOptions"
              item-text="label"
              item-value="id"
              label="Sélectionner un plan approuvé"
              outlined
              :loading="loadingPlans"
              @change="configurationChanged"
            ></v-select>
            <div
              v-if="!loadingPlans && planOptions.length === 0"
              class="text-caption text--secondary"
            >
              Aucun plan approuvé pour le moment. Générez et approuvez d’abord
              un plan dans la section de planification.
            </div>
          </v-col>
        </v-row>

        <v-divider></v-divider>

        <v-row class="mt-3">
          <v-col cols="5">Étapes à exécuter</v-col>
          <v-col cols="7">
            <v-select
              v-model="selectedSteps"
              :items="stepOptions"
              item-text="label"
              item-value="value"
              label="Sélectionner une ou plusieurs étapes"
              multiple
              chips
              deletable-chips
              outlined
              @change="configurationChanged"
            ></v-select>
            <div class="text-caption text--secondary">
              L’ordre d’exécution reste fixe. Qdrant reste désactivé par défaut
              ; il consomme des embeddings OpenAI et a un coût.
            </div>
          </v-col>
        </v-row>

        <v-divider></v-divider>

        <v-row>
          <v-col cols="12">
            <v-checkbox
              v-model="forceApi"
              label="Forcer les appels API et ignorer les caches applicatifs"
              @change="configurationChanged"
            ></v-checkbox>
            <v-checkbox
              v-model="indexQdrant"
              label="Indexer Qdrant à la fin de ce run"
              :disabled="!selectedSteps.includes('qdrant')"
              @change="configurationChanged"
            ></v-checkbox>
          </v-col>
        </v-row>

        <v-checkbox
          v-model="confirmed"
          label="Je confirme le plan sélectionné et le périmètre de ce run"
          @change="updateParams"
        ></v-checkbox>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import axios from "@axios";

export default {
  name: "AdnAudienceBackfillExecute",
  props: {
    initialPlanId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      plans: [],
      loadingPlans: false,
      selectedPlanId: null,
      forceApi: false,
      indexQdrant: false,
      selectedSteps: ["ingest", "post_ga", "ia_stats", "compute"],
      stepOptions: [
        { value: "ingest", label: "Flux Audience → adn_post" },
        { value: "post_ga", label: "GA articles" },
        { value: "ia_stats", label: "Statistiques IA hors Marfeel" },
        { value: "compute", label: "Calculs par période" },
        { value: "qdrant", label: "Indexation Qdrant finale" },
      ],
      confirmed: false,
    };
  },
  computed: {
    planOptions() {
      return this.plans.map((plan) => ({
        id: plan.id,
        label:
          `#${plan.id} — ${plan.domains_json.join(", ")} ` +
          `(${plan.date_from} → ${plan.date_to}, ` +
          `${plan.missing_ids ?? "?"} IDs manquants)`,
      }));
    },
  },
  async created() {
    await this.fetchApprovedPlans();
    if (this.plans.some((plan) => plan.id === this.initialPlanId)) {
      this.selectedPlanId = this.initialPlanId;
    }
    this.updateParams();
  },
  methods: {
    async fetchApprovedPlans() {
      this.loadingPlans = true;
      try {
        const response = await axios.get("/adn-audience-backfill/plans", {
          params: { status: "approved" },
        });
        this.plans = response.data;
      } finally {
        this.loadingPlans = false;
      }
    },
    configurationChanged() {
      this.confirmed = false;
      if (!this.selectedSteps.includes("qdrant")) {
        this.indexQdrant = false;
      }
      this.updateParams();
    },
    updateParams() {
      const errors = [];
      const texts = [];

      if (!this.selectedPlanId) {
        errors.push("Sélectionnez un plan approuvé.");
      } else {
        texts.push(`Plan : #${this.selectedPlanId}`);
      }
      if (!this.selectedSteps.length) {
        errors.push("Sélectionnez au moins une étape à exécuter.");
      }
      if (!this.confirmed) {
        errors.push("Confirmez explicitement le plan et le périmètre.");
      }
      if (this.selectedSteps.includes("qdrant") && !this.indexQdrant) {
        errors.push(
          "Activez explicitement l’indexation Qdrant pour exécuter cette étape."
        );
      }
      if (
        this.selectedSteps.includes("qdrant") &&
        !this.selectedSteps.includes("ingest")
      ) {
        errors.push(
          "L’étape Qdrant nécessite l’ingestion dans le même lancement."
        );
      }

      const orderedSteps = this.stepOptions.filter((step) =>
        this.selectedSteps.includes(step.value)
      );
      texts.push(
        `Étapes : ${orderedSteps.map((step) => step.label).join(" → ")}`
      );
      texts.push(`Force API : ${this.forceApi ? "oui" : "non"}`);
      texts.push(`Qdrant à la fin : ${this.indexQdrant ? "oui" : "non"}`);

      const params = {
        plan_id: this.selectedPlanId,
        force_api: this.forceApi,
        steps: [...this.selectedSteps],
        index_qdrant: this.indexQdrant,
      };
      const command = this.selectedPlanId
        ? `Airflow adn_audience_backfill_execute_dag: plan_id=${this.selectedPlanId}`
        : "";

      this.$emit("onUpdate", { params, errors, texts, command });
    },
  },
};
</script>
